import React, { useEffect, useRef } from "react";
import { useUserContext } from "../../../providers/UserProvider";
import { useSession } from "../../../hooks/useSession/useSession";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";

const COMMON_HEADER_TAG = "opti-common-header";

export const OptiCommonHeader = () => {
    const { instanceId, organizationName } = useUserContext();
    const { accessToken } = useSession();
    const { enabled: enableRebrand } = useFeatureFlag(Flags.ENABLE_REBRAND);
    const background = enableRebrand ? "#FFFFFF" : "#080736";
    const headerRef = useRef<OptiCommonHeaderElement>(null);

    // The token goes over as a JS property rather than the `auth-token` attribute: React writes
    // every custom element prop as an attribute, which puts the JWT into markup that any script on
    // the page can read and that session recorders serialize and ship off-box. Properties are never
    // reflected, so the header's `@Watch("authToken")` still sees the token (and any rotation of it)
    // while nothing lands in the DOM.
    useEffect(() => {
        const header = headerRef.current;
        const registry = window.customElements;
        if (!header || !accessToken || !registry) return;

        // Assigning before the element upgrades would shadow the setter the upgrade installs, so the
        // header would silently never receive the token.
        if (registry.get(COMMON_HEADER_TAG)) {
            header.authToken = accessToken;
            return;
        }

        let cancelled = false;
        registry
            .whenDefined(COMMON_HEADER_TAG)
            .then(() => {
                if (!cancelled) header.authToken = accessToken;
            })
            // Only rejects on an invalid tag name, which a constant can't be — swallow it rather
            // than surface an unhandled rejection.
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    return (
        <div style={{ display: "block", height: 40, background }}>
            {accessToken && (
                <opti-common-header
                    ref={headerRef}
                    instance-id={instanceId}
                    {...(organizationName && { "org-name": organizationName })}
                    {...(enableRebrand && { version: "2" })}
                    sticky
                ></opti-common-header>
            )}
        </div>
    );
};

OptiCommonHeader.displayName = "OptiCommonHeader";
